// Widget „Einsatz“ (Handoff §6.4): Tabs Zeiten · Material · Fahrten · Abnahme · Hausmeister.
// Alle Bereiche bleiben eingehängt (wie bisher alle sichtbar waren) – so stimmen die Zähler in den Tabs,
// und ein Tabwechsel lädt nichts neu. Der Hausmeister-Tab erscheint nur, wenn es Hausmeister gibt.
import { useCallback, useEffect, useState } from 'react';
import { Broom, Car, HardHat, Package, Signature, Timer } from '@phosphor-icons/react';
import { WidgetHuelle } from '../../../../components/ui/WidgetHuelle';
import { Segment, type SegmentEintrag } from '../../../../components/ui/Segment';
import TicketZeiten from '../../../../components/tickets/TicketZeiten';
import type { ZeitnachweisTicket } from '../../../../lib/export/exportZeitnachweisPdf';
import TicketMaterial from '../../../../components/tickets/TicketMaterial';
import TicketFahrten from '../../../../components/tickets/TicketFahrten';
import TicketAbnahmen from '../../../../components/tickets/TicketAbnahmen';
import TicketHausmeister from '../../../../components/tickets/TicketHausmeister';

export type EinsatzTab = 'zeiten' | 'material' | 'fahrten' | 'abnahme' | 'hausmeister';

export interface WidgetEinsatzProps {
  ticketId: string;
  userProfile?: { id?: string; full_name?: string } | null;
  nachweisKopf: ZeitnachweisTicket;
  /** Start-Tab (z. B. „zeiten“ beim Sprung aus der Timer-Pille) */
  startTab?: EinsatzTab;
  /** Zählt hoch, wenn von außen auf „Zeiten“ gesprungen wird (Klick auf die Timer-Pille) */
  sprungZuZeiten?: number;
}

export function WidgetEinsatz({ ticketId, userProfile, nachweisKopf, startTab = 'zeiten', sprungZuZeiten = 0 }: WidgetEinsatzProps) {
  const [tab, setTab] = useState<EinsatzTab>(startTab);
  useEffect(() => { if (sprungZuZeiten > 0) setTab('zeiten'); }, [sprungZuZeiten]);
  const [anzahl, setAnzahl] = useState<Record<EinsatzTab, number>>({ zeiten: 0, material: 0, fahrten: 0, abnahme: 0, hausmeister: 0 });
  const [hausmeister, setHausmeister] = useState(false);
  const zaehler = useCallback((k: EinsatzTab) => (n: number) => setAnzahl((a) => (a[k] === n ? a : { ...a, [k]: n })), []);
  const [zZeiten] = useState(() => zaehler('zeiten'));
  const [zMaterial] = useState(() => zaehler('material'));
  const [zFahrten] = useState(() => zaehler('fahrten'));
  const [zAbnahme] = useState(() => zaehler('abnahme'));
  const [zHausmeister] = useState(() => zaehler('hausmeister'));
  const onVerfuegbar = useCallback((ja: boolean) => {
    setHausmeister(ja);
    if (!ja) setTab((t) => (t === 'hausmeister' ? 'zeiten' : t));
  }, []);

  const tabs: SegmentEintrag<EinsatzTab>[] = [
    { key: 'zeiten', label: 'Zeiten', icon: Timer, iconFarbe: 'var(--acc)', anzahl: anzahl.zeiten, tip: 'Zeiten – Diesen Bereich im Einsatz-Widget anzeigen' },
    { key: 'material', label: 'Material', icon: Package, iconFarbe: 'var(--warn)', anzahl: anzahl.material, tip: 'Material – Diesen Bereich im Einsatz-Widget anzeigen' },
    { key: 'fahrten', label: 'Fahrten', icon: Car, iconFarbe: 'var(--info)', anzahl: anzahl.fahrten, tip: 'Fahrten – Diesen Bereich im Einsatz-Widget anzeigen' },
    { key: 'abnahme', label: 'Abnahme', icon: Signature, iconFarbe: 'var(--ok)', anzahl: anzahl.abnahme, tip: 'Abnahme – Diesen Bereich im Einsatz-Widget anzeigen' },
    ...(hausmeister ? [{ key: 'hausmeister', label: 'Hausmeister', icon: Broom, iconFarbe: '#8a63d2', anzahl: anzahl.hausmeister, tip: 'Hausmeister – Ticket für einen Hausmeister freigeben' } satisfies SegmentEintrag<EinsatzTab>] : []),
  ];

  const bereich = (k: EinsatzTab) => ({ role: 'tabpanel' as const, hidden: tab !== k, 'aria-label': k });

  return (
    <WidgetHuelle titel="Einsatz" icon={HardHat} kopf="kachel" style={{ flexShrink: 0 }}>
      <div className="immo-scroll overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {/* #533: Mit fünf beschrifteten Tabs war die Leiste breiter als die 380-px-Spalte —
            „Hausmeister“ lag unsichtbar rechts außerhalb. Kompakt: nur der aktive Tab mit Text. */}
        <Segment ariaLabel="Einsatz-Bereich" voll kompakt wert={tab} onChange={setTab} eintraege={tabs} style={{ width: '100%' }} />
      </div>
      <div {...bereich('zeiten')}><TicketZeiten ticketId={ticketId} userProfile={userProfile} ticket={nachweisKopf} onAnzahl={zZeiten} /></div>
      <div {...bereich('material')}><TicketMaterial ticketId={ticketId} userProfile={userProfile} onAnzahl={zMaterial} /></div>
      <div {...bereich('fahrten')}><TicketFahrten ticketId={ticketId} userProfile={userProfile} onAnzahl={zFahrten} /></div>
      <div {...bereich('abnahme')}><TicketAbnahmen ticketId={ticketId} userProfile={userProfile} onAnzahl={zAbnahme} /></div>
      <div {...bereich('hausmeister')}><TicketHausmeister ticketId={ticketId} userProfile={userProfile} onAnzahl={zHausmeister} onVerfuegbar={onVerfuegbar} /></div>
    </WidgetHuelle>
  );
}
